import { ReactNode, useEffect, useState } from "react"
import { CardVariant } from "../../models/CardVariant"
import { FormatRules, FormatRulesDictionary, TournamentFormat } from "../../models/TournamentFormat"
import { classNamesOrEmpty } from "../../style/common"

export type CardVariantImageBanOverlayProps = {
    variant: CardVariant,
    tournamentFormat: TournamentFormat,
    formatRules: FormatRulesDictionary,
    grandfatheredByOpus: Record<number, Record<string, true | undefined> | undefined>,
    className?: string | undefined,
    overlay?: ReactNode
}

export function CardVariantImageBanOverlay(props: CardVariantImageBanOverlayProps) {
    const [isBanned, setIsBanned] = useState(false);
    const [isRestricted, setIsRestricted] = useState(false);

    useEffect(() => {
        if (!props.variant || !props.formatRules || !props.tournamentFormat || !Object.keys(props.formatRules).length) {
            return;
        }

        const newIsBanned = getIsBanned({
            formatRules: props.formatRules[props.tournamentFormat],
            tournamentFormat: props.tournamentFormat,
            variant: props.variant,
            grandfatheredByOpus: props.grandfatheredByOpus
        });

        const newIsRestricted = getIsRestricted({
            formatRules: props.formatRules[props.tournamentFormat],
            variant: props.variant
        });

        setIsBanned(newIsBanned);
        setIsRestricted(newIsRestricted)
    }, [props.tournamentFormat, props.formatRules, props.variant]);

    if (!isBanned && !isRestricted) {
        return <></>;
    }

    return (
        <>
            {
                !!props.overlay &&
                props.overlay
            }
            <div className={`absolute top-0 bottom-0 left-0 right-0 ${classNamesOrEmpty(props.className)}`}>
                {
                    isBanned &&
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 858 1200"
                        preserveAspectRatio="xMinYMid meet"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <text
                            x="210"
                            y="450"
                            font-size="125"
                            fill="red"
                            fontWeight="600"
                            style={{ filter: "drop-shadow(3px 5px 2px black)" }}>
                            Banned
                        </text>
                    </svg>
                }
                {
                    isRestricted &&
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 858 1200"
                        preserveAspectRatio="xMinYMid meet"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <text
                            x="140"
                            y="450"
                            font-size="125"
                            fill="red"
                            fontWeight="600"
                            style={{ filter: "drop-shadow(3px 5px 2px black)" }}>
                            Restricted
                        </text>
                    </svg>
                }
            </div>
        </>
    )
}

export type GetBanReportProps = {
    variants: CardVariant[],
    tournamentFormat: TournamentFormat,
    formatRules: FormatRulesDictionary,
    grandfatheredByOpus: Record<number, Record<string, true | undefined> | undefined>
}

export function getBanReport(props: GetBanReportProps): CardVariant[] {
    const result: CardVariant[] = [];
    const rules = props.formatRules[props.tournamentFormat];

    for (const variant of props.variants) {
        const isBanned = getIsBanned({
            formatRules: rules,
            tournamentFormat: props.tournamentFormat,
            variant: variant,
            grandfatheredByOpus: props.grandfatheredByOpus
        });

        if (isBanned) {
            result.push(variant);
        }
    }

    return result;
}

export function getRestrictedReport(props: GetBanReportProps): CardVariant[] {
    const result: CardVariant[] = [];
    const rules = props.formatRules[props.tournamentFormat];

    for (const variant of props.variants) {
        const isRestricted = getIsRestricted({
            formatRules: rules,
            variant: variant
        });

        if (isRestricted) {
            result.push(variant);
        }
    }

    return result;
}

export function getIsBanned(props: {
    variant: CardVariant,
    tournamentFormat: TournamentFormat,
    formatRules: FormatRules,
    grandfatheredByOpus: Record<number, Record<string, true | undefined> | undefined>
}): boolean {
    const isAllowedCategory = !(props.formatRules?.banned?.categories ?? []).every(x => props.variant.card.categories?.includes(x))
        || (props.formatRules?.allowed?.additionalCategories ?? []).some(x => props.variant.card.categories?.includes(x));

    const isAllowedCard = (props.formatRules?.allowed?.cards ?? []).some(x => x === props.variant.serial || x === props.variant.altSerial);
    const isAllowedOpus = (props.formatRules?.allowed?.opus ?? []).some(x => x === props.variant.opus || x === props.variant.altOpus);
    const isAllowedRarity = (props.formatRules?.allowed?.rarities || []).some(x => x === props.variant?.card?.rarity);
    const isBannedCard = (props.formatRules?.banned?.cards ?? []).some(x => x === props.variant.serial || x === props.variant.altSerial);

    const isGrandfathered = (props.formatRules?.allowed?.opus ?? []).some((opus) => {
        const variantsInOpus = props.grandfatheredByOpus[opus];

        if (!variantsInOpus) {
            return false;
        }

        return !!variantsInOpus[props.variant.id];
    });

    let newIsBanned = false;
    if (isBannedCard) {
        newIsBanned = true;
    } else if (props.tournamentFormat === "Title" && !isAllowedCategory && !isAllowedCard) {
        newIsBanned = true;
    } else if ((props.tournamentFormat === "L3" || props.tournamentFormat === "L6" || props.tournamentFormat === "F5") && !isAllowedOpus && !isAllowedCard && !isGrandfathered) {
        newIsBanned = true;
    } else if (props.tournamentFormat === "Pauper" && !isAllowedRarity) {
        newIsBanned = true;
    }

    return newIsBanned;
}

export function getIsRestricted(props: {
    variant: CardVariant,
    formatRules: FormatRules
}): boolean {
    return (props.formatRules?.restrictions ?? []).some(x => x.serial === props.variant.serial || x.serial === props.variant.altSerial);
}